import { type FC, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import PresenceContext from '../context/PresenceContext';
import useAuth from '@/features/auth/hooks/useAuth';
import { authStorage } from '@/features/auth/config/auth-storage';

const PresenceProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { state: authState, user } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const [socketVersion, setSocketVersion] = useState(0);

  useEffect(() => {
    // Only connect when authenticated
    if (authState !== 'loggedin') {
      return;
    }

    const accessToken = authStorage.getItem('access_token');
    if (!accessToken) {
      return;
    }

    // Create Socket.IO connection
    const socket = io('/presence', {
      auth: { token: `Bearer ${accessToken}` },
      transports: ['websocket', 'polling'],
      reconnection: true,
    });

    socketRef.current = socket;
    setSocketVersion(v => v + 1);

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', () => {
      setIsConnected(false);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setSocketVersion(v => v + 1);
      setIsConnected(false);
    };
  }, [authState]);

  const currentUserId = user?.id ?? null;

  // socketVersion triggers re-computation when socket changes
  const contextValue = useMemo(
    () => ({
      socket: socketRef.current,
      isConnected,
      currentUserId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socketVersion, isConnected, currentUserId]
  );

  return <PresenceContext.Provider value={contextValue}>{children}</PresenceContext.Provider>;
};

export default PresenceProvider;
